import { css } from '@styled-system/css';
import { type FC, useMemo } from 'react';

import amplitude from '@/amplitude';
import type { ProductItemCondition, ProductSort } from '@/shared/api/types/generated';
import { Dropdown, type DropdownOption } from '@/shared/components/ui/Dropdown';
import { getAvailableItemConditions } from '@/shared/utils/analysis/itemCondition';
import { getAvailableProductSorts } from '@/shared/utils/analysis/productSort';

import type { SellHistoryFilters as Filters } from '../../../features/analysisPageSellsHist/types';

interface SellHistoryFiltersProps {
  filters: Filters;
  availableBrands: Array<{ brand: string; count: number }>;
  availableConditions: ProductItemCondition[];
  onBrandChange: (brand?: string) => void;
  onConditionChange: (condition?: ProductItemCondition) => void;
  onSortChange: (sort: ProductSort) => void;
}

export const SellHistoryFilters: FC<SellHistoryFiltersProps> = ({
  filters,
  availableBrands,
  availableConditions,
  onBrandChange,
  onConditionChange,
  onSortChange,
}) => {
  const filtersContainerStyles = css({
    display: 'flex',
    gap: 'sm',
    alignItems: 'center',
    w: 'fit-content',
  });

  // Options pour les dropdowns
  const brandOptions = useMemo(() => {
    const brandOptions = availableBrands.map((brand) => ({
      value: brand.brand,
      label: `${brand.brand} (${brand.count})`,
    }));

    // Ajouter l'option "Toutes les marques" en première position
    return [{ value: 'all', label: 'Toutes les marques' }, ...brandOptions];
  }, [availableBrands]);

  const conditionOptions = useMemo(() => {
    const conditionOptions = getAvailableItemConditions().filter((option) =>
      availableConditions.includes(option.value as ProductItemCondition),
    );

    // Ajouter l'option "Tous les états" en première position
    return [{ value: 'all', label: 'Tous les états' }, ...conditionOptions];
  }, [availableConditions]);

  const sortOptions = useMemo(() => getAvailableProductSorts(), []);

  const handleBrandChange = (option: DropdownOption) => {
    // Tracker l'événement avec Amplitude
    amplitude.track('TEST Historique Filter Marques', { brand: option.value });

    onBrandChange(option.value === 'all' ? undefined : option.value);
  };

  const handleConditionChange = (option: DropdownOption) => {
    // Tracker l'événement avec Amplitude
    amplitude.track('TEST Historique Filter Etats', { itemCondition: option.value });

    onConditionChange(option.value === 'all' ? undefined : (option.value as ProductItemCondition));
  };

  const handleSortChange = (option: DropdownOption) => {
    // Tracker l'événement avec Amplitude
    amplitude.track('TEST Historique Filter Sort', { sort: option.value });

    onSortChange(option.value as ProductSort);
  };

  return (
    <div className={filtersContainerStyles}>
      <Dropdown
        options={brandOptions}
        value={filters.brand || 'all'}
        onSelect={handleBrandChange}
        placeholder="Toutes les marques"
        menuWidth="12rem"
        maxHeight="20rem"
      />

      <Dropdown
        options={conditionOptions}
        value={filters.itemCondition ?? 'all'}
        onSelect={handleConditionChange}
        placeholder="Tous les états"
        menuWidth="12rem"
        maxHeight="20rem"
      />

      <Dropdown
        options={sortOptions}
        value={filters.sort || 'newest'}
        onSelect={handleSortChange}
        placeholder="Trier par"
        menuWidth="14rem"
        maxHeight="20rem"
      />
    </div>
  );
};
